import { MouseEvent, ReactNode, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { contactItems, emailUrl, kakaoInquiryUrl, phoneUrl, youtubeUrl } from "../../data/specialTour";

const navItems = [
  { label: "지역", to: "/regions" },
  { label: "숙소", to: "/hotels" },
  { label: "일정", to: "/itineraries" },
  { label: "골프장", to: "/courses" },
  { label: "관광지", to: "/destinations" },
  { label: "파크골프", to: "/park-golf" },
  { label: "후기", to: "/reviews" },
  { label: "문의", to: "/contact" },
];

export function SiteHeader() {
  return (
    <header className="sticky top-0 z-50 border-b border-black/10 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-3">
          <img
            src="/special-tour-assets/special-tour-logo.jpg"
            alt="스페셜투어"
            className="h-9 w-9 rounded-lg object-cover"
          />
          <div className="min-w-0">
            <div className="text-sm font-900 leading-tight text-[#15391f]">스페셜투어</div>
            <div className="text-[11px] font-600 uppercase tracking-normal text-[#68766b]">Japan Golf Tour</div>
          </div>
        </Link>
        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  "rounded-lg px-3 py-2 text-sm font-700 transition",
                  "min-w-[52px] text-center whitespace-nowrap",
                  isActive ? "bg-[#e8f2e6] text-[#173f22]" : "text-[#374139] hover:bg-[#f1f4ef]",
                ].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <a
          href={kakaoInquiryUrl}
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#FEE500] px-3 text-sm font-900 text-[#191600] shadow-sm transition hover:bg-[#f3db00]"
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
          문의
        </a>
      </div>
      <div className="flex gap-1 overflow-x-auto border-t border-black/5 px-3 py-2 md:hidden">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                "min-w-[52px] whitespace-nowrap rounded-lg px-3 py-2 text-center text-sm font-700",
                isActive ? "bg-[#e8f2e6] text-[#173f22]" : "text-[#374139]",
              ].join(" ")
            }
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="bg-[#102517] text-white">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 md:grid-cols-[1.2fr_1fr] lg:px-8">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <img
              src="/special-tour-assets/special-tour-logo.jpg"
              alt="스페셜투어"
              className="h-12 w-12 rounded-lg object-cover"
            />
            <div>
              <div className="text-xl font-900">스페셜투어</div>
              <div className="text-sm font-500 text-white/70">일본 골프 전문 맞춤 투어</div>
            </div>
          </div>
          <p className="max-w-xl text-sm leading-7 text-white/72">
            골프만 실컷 치고 싶은 팀부터 자유여행을 함께 넣고 싶은 팀까지, 일본 현지 동선에 맞춰 일정을
            세심하게 설계합니다.
          </p>
        </div>
        <div className="grid gap-3 text-sm">
          {contactItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="flex items-center justify-between rounded-lg border border-white/12 px-4 py-3 text-white/85 transition hover:border-white/30 hover:bg-white/10"
            >
              <span className="font-700">{item.label}</span>
              <span className="text-right text-white/68">{item.value}</span>
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
}

export function SpecialTourLayout({ children }: { children: ReactNode }) {
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (!lightboxImage) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLightboxImage(null);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightboxImage]);

  const handleContentImageClick = (event: MouseEvent<HTMLElement>) => {
    const target = event.target;

    if (!(target instanceof HTMLImageElement)) {
      return;
    }

    const src = target.currentSrc || target.src;

    if (!src) {
      return;
    }

    event.preventDefault();
    setLightboxImage({ src, alt: target.alt });
  };

  return (
    <div className="min-h-screen bg-[#f7f5ef] text-[#18231c]">
      <SiteHeader />
      <main className="[&_img]:cursor-zoom-in" onClick={handleContentImageClick}>
        {children}
      </main>
      <SiteFooter />
      {lightboxImage ? (
        <div
          className="fixed inset-0 z-[100] bg-black/82 p-4 backdrop-blur-sm sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label="전체 이미지 보기"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-lg bg-white px-4 py-2 text-sm font-900 text-[#15391f] shadow-sm"
            onClick={() => setLightboxImage(null)}
          >
            닫기
          </button>
          <div className="flex h-full w-full items-center justify-center">
            <img
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              className="max-h-[88vh] max-w-full cursor-zoom-out rounded-lg object-contain shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function PageIntro({
  eyebrow,
  title,
  body,
  afterBody,
  image,
}: {
  eyebrow: string;
  title: string;
  body: string;
  afterBody?: ReactNode;
  image?: string;
}) {
  return (
    <section className="bg-[#f7f5ef]">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-[1fr_360px] lg:px-8">
        <div className="flex flex-col justify-center">
          <p className="mb-3 text-sm font-900 uppercase tracking-normal text-[#d16339]">{eyebrow}</p>
          <h1 className="max-w-3xl text-4xl font-900 leading-tight text-[#15391f] [word-break:keep-all] md:text-5xl">{title}</h1>
          <p className="mt-5 max-w-2xl text-base leading-8 text-[#526054] [word-break:keep-all] md:text-lg">{body}</p>
          {afterBody ? <div className="mt-6 max-w-2xl">{afterBody}</div> : null}
        </div>
        {image ? (
          <img src={image} alt="" className="h-64 w-full rounded-lg object-cover shadow-sm md:h-80" />
        ) : null}
      </div>
    </section>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  body,
}: {
  eyebrow?: string;
  title: ReactNode;
  body?: ReactNode;
}) {
  return (
    <div className="mb-8 max-w-3xl">
      {eyebrow ? <p className="mb-2 text-sm font-900 uppercase tracking-normal text-[#d16339]">{eyebrow}</p> : null}
      <h2 className="text-3xl font-900 leading-tight text-[#15391f] [word-break:keep-all] md:text-4xl">{title}</h2>
      {body ? <p className="mt-4 text-base leading-7 text-[#596559] [word-break:keep-all]">{body}</p> : null}
    </div>
  );
}

export function ContentBand({
  children,
  className = "",
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={`px-4 py-12 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </section>
  );
}

export function Inner({ children }: { children: ReactNode }) {
  return <div className="mx-auto max-w-7xl">{children}</div>;
}

export function KakaoButton({ label = "카카오톡으로 견적 문의" }: { label?: string }) {
  return (
    <a
      href={kakaoInquiryUrl}
      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#FEE500] px-5 text-sm font-900 text-[#1b1600] shadow-sm transition hover:bg-[#f3db00]"
    >
      <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
      {label}
      <ArrowForwardIcon sx={{ fontSize: 18 }} />
    </a>
  );
}

export function QuickContactBar() {
  return (
    <div className="flex flex-wrap gap-3">
      <KakaoButton />
      <a
        href={phoneUrl}
        className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#c9d3c5] bg-white px-5 text-sm font-800 text-[#193a22] transition hover:border-[#8faa87]"
      >
        <PhoneOutlinedIcon sx={{ fontSize: 19 }} />
        전화 문의
      </a>
      <a
        href={emailUrl}
        className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#c9d3c5] bg-white px-5 text-sm font-800 text-[#193a22] transition hover:border-[#8faa87]"
      >
        <EmailOutlinedIcon sx={{ fontSize: 19 }} />
        이메일 문의
      </a>
      <a
        href={youtubeUrl}
        className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-[#c9d3c5] bg-white px-5 text-sm font-800 text-[#193a22] transition hover:border-[#8faa87]"
      >
        <YouTubeIcon sx={{ fontSize: 21 }} />
        YouTube
      </a>
    </div>
  );
}
